"use client";

import EmailIcon from "@/icons/email";
import { BASE_URL, CALLBACK_URL, SIGNUP_API, VALIDATE_SIGNUP_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { Loader2Icon } from "lucide-react";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { FormEvent, ReactNode, useState } from "react";
import { toast } from "react-hot-toast";
import OTPInput from "react-otp-input";
import { ModalType } from "@/types/modal-type";

const SignUpForm = ({
	title,
	onSuccess,
	onUserExists,
}: {
	title?: ReactNode;
	onSuccess?: () => void;
	onUserExists?: (email: string) => void;
}) => {
	const params = useParams();
	const pathname = usePathname();

	const [code, setCode] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showVerificationStep, setShowVerificationStep] = useState<boolean>(false);
	const { closeModal } = useModal();

	const sendOTP = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
		if (!emailRegex.test(email)) {
			toast.remove();
			toast.error("Please enter a valid email address");
			return;
		}

		setIsLoading(true);

		const url = `${BASE_URL}/${pathname}`;
		const formattedEmail = email.toLowerCase();

		const validateEmailResponse = await fetch(VALIDATE_SIGNUP_API, {
			method: "POST",
			body: JSON.stringify({ name, email: formattedEmail, fromUrl: url }),
		});

		if (validateEmailResponse.ok) {
			const signupResponse = await signIn("email", {
				email: formattedEmail,
				redirect: false,
			});

			if (signupResponse?.ok) {
				setShowVerificationStep(true);
				toast.success("OTP sent to your email");
			} else {
				toast.error(signupResponse?.error || "Something went wrong");
				console.log("Error occurred during sign up", signupResponse?.error);
			}
		} else {
			const { error, message } = await validateEmailResponse.json();

			if (error === "USER_ALREADY_EXISTS" && onUserExists) {
				onUserExists(formattedEmail);
			} else {
				// Show error for other cases
				toast.error(message || "Something went wrong");
			}
		}

		setIsLoading(false);
	};

	const handleSignup = async () => {
		try {
			if (code.length !== 6) {
				toast.error("Enter a valid OTP");
				return;
			}

			setIsLoading(true);

			const verificationUrl = new URL(`${BASE_URL}/api/auth/callback/email`);
			verificationUrl.searchParams.set("email", email);
			verificationUrl.searchParams.set("token", code);

			if (CALLBACK_URL) {
				verificationUrl.searchParams.set("callbackUrl", CALLBACK_URL);
			}

			const verificationResponse = await fetch(verificationUrl);
			if (verificationResponse.ok) {
				const response = await fetch(SIGNUP_API, {
					method: "POST",
					body: JSON.stringify({ name, email, provider: "Email" }),
				});

				const { message } = await response.json();

				if (response.status === 201) {
					onSuccess?.();
					closeModal(ModalType.Auth);

					if (params.privateDiscussionBoardSlug) {
						window.rudderanalytics?.track("New User SignUp", {
							name: name,
							email: email,
							url: `${BASE_URL}/${pathname}`,
							fromSource: "Private Discussion Board",
							discussionBoardSlug: params.privateDiscussionBoardSlug.toString(),
						});

						const urlSearchParams = new URLSearchParams();
						urlSearchParams.set("from", "private-discussion-board");
						urlSearchParams.set("id", params.privateDiscussionBoardSlug.toString());

						const updateProfileRoute = `/account/profile?${urlSearchParams.toString()}`;

						window.location.href = updateProfileRoute;
					} else {
						window.rudderanalytics?.track("New User SignUp", {
							name: name,
							email: email,
							url: `${BASE_URL}/${pathname}`,
						});

						toast.success(message);
						window.location.href = pathname;
					}

					return;
				}

				toast.error(message);
			} else {
				toast.error("OTP verification failed, please retry");
			}
		} catch (error) {
			console.log("Something went wrong during signup");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			{title ? <h2 className="mb-6 text-xl font-semibold md:text-2xl">{title}</h2> : null}

			{!showVerificationStep ? (
				<form onSubmit={sendOTP}>
					<div className="mb-3">
						<label htmlFor="name" className="mb-2 block text-sm text-gray-600">
							Name
						</label>

						<div className="flex items-center rounded-md border p-2 transition focus-within:border-blue-500">
							<input
								id="name"
								type="text"
								name="name"
								value={name}
								required
								placeholder="Enter Your Name"
								onChange={(e) => {
									const { value } = e.target;
									// Matches basic Latin letters (a-z, A-Z), spaces, hyphens, apostrophes, and common accented characters
									const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/;
									if (nameRegex.test(value) || value === "") {
										if (value.length > 50) {
											toast.remove();
											toast.error("Name should not exceed 50 characters");
										} else if (
											value
												.trim()
												.split(/\s+/)
												.every((part) => part.length <= 20)
										) {
											setName(value);
											toast.remove();
										} else {
											toast.remove();
											toast.error("Name should not exceed 20 characters per word");
										}
									} else {
										toast.remove();
										toast.error(
											"Name should contain only alphabets, spaces, hyphens, and apostrophes"
										);
									}
								}}
								className="w-full rounded-md bg-transparent px-2 text-sm outline-none transition-all duration-300 ease-in-out"
							/>
						</div>
					</div>

					<div className="mb-5">
						<label htmlFor="email" className="mb-2 block text-sm text-gray-600">
							Email
						</label>

						<div className="flex items-center rounded-md border p-2 transition focus-within:border-blue-500">
							<span>
								<EmailIcon className="h-5 w-5" />
							</span>
							<input
								id="email"
								type="email"
								name="email"
								value={email}
								required
								placeholder="Enter your email"
								onChange={(e) => setEmail(e.target.value.toLowerCase())}
								className="w-full rounded-md bg-transparent px-2 text-sm outline-none transition-all duration-300 ease-in-out"
							/>
						</div>
					</div>

					<p className="mb-1 text-xs text-red-500">
						NOTE: Create user profiles only. Company pages can be submitted{" "}
						<span>
							<Link href="/products/form/getting-started" className="text-brand-primary underline">
								here
							</Link>
						</span>
					</p>

					<p className="mb-3 text-xs text-red-500">
						We protect your data in line with GDPR. Your information stays private and is never
						shared or sold. See our{" "}
						<Link
							href="/privacy-policy"
							target="_blank"
							rel="noopener noreferrer"
							className="text-brand-primary underline"
						>
							[Privacy Policy]
						</Link>
						.
					</p>

					<button
						type="submit"
						disabled={isLoading}
						className={cn(
							"flex w-full items-center justify-center gap-1 rounded-md px-2 py-3 text-sm font-semibold transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<Loader2Icon className="h-4 w-4 animate-spin stroke-gray-600" />
							</div>
						) : null}
						<span>{isLoading ? "Sending OTP" : "Get Started"}</span>
					</button>
				</form>
			) : (
				<>
					<div className="mb-4">
						<label htmlFor="otp" className="mb-2 block text-sm">
							Enter OTP
							<p className="text-xxs text-gray-400">Please enter the OTP sent to your email</p>
						</label>
						<div className="flex items-center justify-center rounded bg-brand-primary/10 py-8">
							<OTPInput
								value={code}
								numInputs={6}
								shouldAutoFocus
								onChange={setCode}
								inputType="tel"
								containerStyle="gap-1 flex"
								inputStyle="border rounded-md border-[#B0CAF6] focus:outline-brand-primary text-brand-primary basis-full sm:py-2 text-sm sm:text-lg w-10 h-10 sm:w-14 sm:h-14 text-center"
								renderInput={(props, index) => (
									<>
										<input
											{...props}
											placeholder="0"
											style={{
												appearance: "none",
												MozAppearance: "textfield",
												WebkitAppearance: "none",
											}}
										/>

										{index === 2 && <span className="mx-2 text-xl font-bold text-gray-400">-</span>}
									</>
								)}
							/>
						</div>
					</div>

					<button
						onClick={handleSignup}
						disabled={isLoading}
						className={cn(
							"flex w-full items-center justify-center gap-1 rounded-md px-2 py-2 text-sm font-semibold transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<Loader2Icon className="h-4 w-4 animate-spin stroke-gray-600" />
							</div>
						) : null}
						<span>{isLoading ? "Verifying OTP" : "Sign Up"}</span>
					</button>
				</>
			)}
		</>
	);
};

export default SignUpForm;
